import IconMlDocument from 'components/IconColor/32px/ml_document';
import IconPdfDocument from 'components/IconColor/32px/pdf';
import IconZipDocument from 'components/IconColor/32px/zip';
import Overlay from 'components/Layout/Overlay';
import SortableHandler from 'components/Sortable/SortableHandle';
import {ComponentProps, forwardRef, useRef, useState} from 'react';
import {t} from 'ttag';
import {PermissionResponse} from 'types/permissions';
import {mergeRefs} from 'utils';
import {useModalMouseEscape} from 'utils/modal';

import SupersendPopover from '../../Popover';
import SupersendDocumentIcons from '../SupersendDocumentIcons';
import VerticalDots from '../VerticalDots';

export type SupersendDocumentProps = {
    displayName: string;
    isEditing?: boolean;
    kind: 'document' | 'pdf' | 'zip';
    mid: string;
    permissions?: {
        canViewPdf: PermissionResponse;
    };
    popover?: Omit<ComponentProps<typeof SupersendPopover>, 'onClose'>;
    total?: string;
} & SupersendDocumentAdditionalProps;

type SupersendDocumentAdditionalProps = ComponentProps<
    typeof SupersendDocumentIcons
>;

const SupersendDocument = forwardRef<HTMLDivElement, SupersendDocumentProps>(
    (
        {
            datePaid,
            dateSent,
            dateSentByPost,
            dateViewed,
            displayName,
            isEditing,
            kind,
            mid,
            popover,
            shouldDisplayNotViewed,
            shouldDisplayNotYetSendbypost,
            total,
        },
        ref,
    ) => {
        const [isPopupOpen, setIsPopupOpen] = useState(false);
        const elementRef = useRef<HTMLDivElement>(null);
        useModalMouseEscape({
            enabled: true,
            exclude: [elementRef],
            isOpen: isPopupOpen,
            onClose: () => setIsPopupOpen(false),
        });

        return (
            <>
                <div
                    className="flex w-fit items-start gap-1 rounded border border-grey-400 bg-grey-100 p-2"
                    onClick={() => setIsPopupOpen(!isPopupOpen)}
                    onKeyDown={() => {}}
                    ref={mergeRefs(ref, elementRef)}
                    role="button"
                    tabIndex={0}
                >
                    <div className="flex h-10 items-center gap-1">
                        {isEditing && (
                            <SortableHandler id={mid}>
                                <VerticalDots />
                            </SortableHandler>
                        )}
                        {kind === 'document' && <IconMlDocument />}
                        {kind === 'pdf' && <IconPdfDocument />}
                        {kind === 'zip' && <IconZipDocument />}
                    </div>
                    <div className="flex flex-col text-sm">
                        <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-0.5">
                                {displayName && <span>{displayName}</span>}
                                <SupersendDocumentIcons
                                    datePaid={datePaid}
                                    dateSent={dateSent}
                                    dateSentByPost={dateSentByPost}
                                    dateViewed={dateViewed}
                                    shouldDisplayNotViewed={
                                        shouldDisplayNotViewed
                                    }
                                    shouldDisplayNotYetSendbypost={
                                        shouldDisplayNotYetSendbypost
                                    }
                                />
                            </span>
                        </div>
                        {total && <span>{t`Total : ${total}`}</span>}
                    </div>
                </div>
                {popover && isPopupOpen && (
                    <Overlay
                        className="clickable"
                        element={elementRef.current}
                        layoutLayer="z-modal"
                        layoutMethod="bottom"
                        layoutOffset={4}
                    >
                        <SupersendPopover
                            {...popover}
                            onClose={() => setIsPopupOpen(false)}
                        />
                    </Overlay>
                )}
            </>
        );
    },
);
SupersendDocument.displayName = 'SupersendDocument';
export default SupersendDocument;
